import { FC } from 'react'
import { Controller } from 'react-hook-form'
import {
  Box,
  Typography,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormHelperText,
} from '@mui/material'
import { useQuestionsBoxLogic } from './QuestionsBox.logic'
import { QuestionsBoxProps } from './QuestionsBox.types'
import { questions } from './QuestionsBox.logic'
import { DateInput } from 'components/mui'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'lib/dayjs'

export const QuestionsBox: FC<QuestionsBoxProps> = () => {
  const {
    control,
    watch,
    formState: { errors },
    handleSubmitForm,
  } = useQuestionsBoxLogic()

  const adverseEvent = watch('adverseEvent')
  const sourceFacilityNotified = watch('sourceFacilityNotified')

  return (
    <form onSubmit={handleSubmitForm}>
      <Box sx={{ mb: 2, p: 2, border: '1px dashed #e0e0e0', borderRadius: 1 }}>
        <Typography variant="h3" sx={{ mb: 2 }}>
          Implant Record Questions
        </Typography>

        <FormControl component="fieldset" fullWidth sx={{ mt: 2 }}>
          <FormLabel>{questions[0].question}</FormLabel>
          <Controller
            name="mdBroughtImplant"
            control={control}
            defaultValue={questions[0].defaultValue}
            rules={{ required: 'This field is required' }}
            render={({ field: { value, ...field } }) => (
              <FormControl error={!!errors.mdBroughtImplant}>
                <RadioGroup row {...field}>
                  <FormControlLabel
                    value="YES"
                    control={<Radio />}
                    label="Yes"
                    checked={value === 'YES'}
                  />
                  <FormControlLabel
                    value="NO"
                    control={<Radio />}
                    label="No"
                    checked={value === 'NO'}
                  />
                </RadioGroup>
                {errors.mdBroughtImplant?.message && (
                  <FormHelperText>
                    {errors.mdBroughtImplant.message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />
        </FormControl>

        <FormControl component="fieldset" fullWidth sx={{ mt: 2 }}>
          <FormLabel>{questions[1].question}</FormLabel>
          <Controller
            name="isProductRecall"
            control={control}
            defaultValue={questions[1].defaultValue}
            rules={{ required: 'This field is required' }}
            render={({ field: { value, ...field } }) => (
              <FormControl error={!!errors.isProductRecall}>
                <RadioGroup row {...field}>
                  <FormControlLabel
                    value="YES"
                    control={<Radio />}
                    label="Yes"
                    checked={value === 'YES'}
                  />
                  <FormControlLabel
                    value="NO"
                    control={<Radio />}
                    label="No"
                    checked={value === 'NO'}
                  />
                </RadioGroup>
                {errors.isProductRecall?.message && (
                  <FormHelperText>
                    {errors.isProductRecall.message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />
        </FormControl>

        <FormControl component="fieldset" fullWidth sx={{ mt: 2 }}>
          <FormLabel>{questions[2].question}</FormLabel>
          <Controller
            name="isProductMalfunction"
            control={control}
            defaultValue={questions[2].defaultValue}
            rules={{ required: 'This field is required' }}
            render={({ field: { value, ...field } }) => (
              <FormControl error={!!errors.isProductMalfunction}>
                <RadioGroup row {...field}>
                  <FormControlLabel
                    value="YES"
                    control={<Radio />}
                    label="Yes"
                    checked={value === 'YES'}
                  />
                  <FormControlLabel
                    value="NO"
                    control={<Radio />}
                    label="No"
                    checked={value === 'NO'}
                  />
                </RadioGroup>
                {errors.isProductMalfunction?.message && (
                  <FormHelperText>
                    {errors.isProductMalfunction.message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />
        </FormControl>

        <FormControl fullWidth sx={{ mt: 1 }}>
          <Controller
            name="adverseEvent"
            control={control}
            defaultValue={null}
            rules={{ required: 'Adverse event identification is required' }}
            render={({ field: { onChange, value, ...field } }) => (
              <FormControl
                fullWidth
                error={!!errors.adverseEvent}
                sx={{ mt: 2 }}
              >
                <InputLabel>Adverse Event Identification*</InputLabel>
                <Select
                  onChange={onChange}
                  value={value}
                  label="Adverse Event Identification*"
                  inputProps={{
                    ...field,
                    placeholder: 'Select',
                  }}
                  defaultValue={null}
                >
                  <MenuItem value="YES">Yes</MenuItem>
                  <MenuItem value="NO">No</MenuItem>
                  <MenuItem value="NA">NA</MenuItem>
                </Select>
                {errors.adverseEvent?.message && (
                  <FormHelperText>{errors.adverseEvent.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </FormControl>

        {adverseEvent === 'YES' && (
          <>
            <Controller
              name="implantSource"
              control={control}
              rules={{
                required: 'Source is required when adverse event is Yes',
              }}
              render={({ field: { onChange, value, ...field } }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Implant Source*"
                  variant="outlined"
                  error={!!errors.implantSource}
                  helperText={errors.implantSource?.message}
                  onChange={onChange}
                  value={value}
                  sx={{ mt: 2 }}
                />
              )}
            />

            <Controller
              name="sourceFacilityNotified"
              control={control}
              rules={{
                required:
                  'Source facility notification is required when adverse event is Yes',
              }}
              render={({ field: { onChange, value, ...field } }) => (
                <FormControl
                  fullWidth
                  error={!!errors.sourceFacilityNotified}
                  sx={{ mt: 2 }}
                >
                  <InputLabel>Source Facility Notified*</InputLabel>
                  <Select
                    onChange={onChange}
                    value={value}
                    label="Source Facility Notified*"
                    inputProps={{
                      ...field,
                      placeholder: 'Select',
                    }}
                    defaultValue="NA"
                  >
                    <MenuItem value="YES">Yes</MenuItem>
                    <MenuItem value="NO">No</MenuItem>
                    <MenuItem value="NA">NA</MenuItem>
                  </Select>
                  {errors.sourceFacilityNotified?.message && (
                    <FormHelperText>
                      {errors.sourceFacilityNotified.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />

            {sourceFacilityNotified === 'YES' && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Controller
                    name="notificationDate"
                    control={control}
                    rules={{
                      required: 'Date is required when facility is notified',
                    }}
                    render={({ field: { onChange, value, ...field } }) => (
                      <DateInput
                        placeholder="MM/DD/YYYY"
                        format="MM/DD/YYYY"
                        readOnly
                        openTo="month"
                        handleChangeFormData={onChange}
                        label="Notification Date*"
                        value={value}
                        error={!!errors.notificationDate}
                        variant="outlined"
                        helperText={errors.notificationDate?.message}
                        fullWidth
                        {...field}
                      />
                    )}
                  />

                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Controller
                      name="notificationTime"
                      control={control}
                      rules={{
                        required: 'Time is required when facility is notified',
                      }}
                      render={({ field }) => {
                        const { onChange, value, ref, ...rest } = field
                        const valueAsDayjs = value ? dayjs(value) : null

                        return (
                          <FormControl
                            fullWidth
                            // error={!!errors.notificationTime}
                          >
                            {/* <InputLabel>Notification Time*</InputLabel> */}
                            <TimePicker
                              slotProps={{
                                textField: {
                                  error: !!errors.notificationTime,
                                },
                              }}
                              value={valueAsDayjs}
                              onChange={(newValue: dayjs.Dayjs | null) => {
                                onChange(newValue ? newValue.toDate() : null)
                              }}
                              label="Notification Time*"
                              {...rest}
                            />
                            {errors.notificationTime && (
                              <Typography
                                color="error"
                                variant="caption"
                                ml={2}
                              >
                                {errors.notificationTime.message}
                              </Typography>
                            )}
                          </FormControl>
                        )
                      }}
                    />
                  </LocalizationProvider>
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </form>
  )
}
